/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { riskPolicyChallengesDecline } from "../funcs/riskPolicyChallengesDecline.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { DeclineRiskPolicyChallengeResponseBody } from "../models/components/declineriskpolicychallengeresponsebody.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DeclineRiskPolicyChallengeRequest,
  DeclineRiskPolicyChallengeSecurity,
} from "../models/operations/declineriskpolicychallenge.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RiskDeclinePolicyChallengeMutationVariables = {
  request: DeclineRiskPolicyChallengeRequest;
  security?: DeclineRiskPolicyChallengeSecurity | undefined;
  options?: RequestOptions;
};

export type RiskDeclinePolicyChallengeMutationData =
  DeclineRiskPolicyChallengeResponseBody;

export type RiskDeclinePolicyChallengeMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * declineRiskPolicyChallenge risk
 *
 * @remarks
 * Decline a risk policy warn/challenge from a warning-link token: invalidate the link and mark the challenge declined. The blocked action stays blocked.
 */
export function useRiskDeclinePolicyChallengeMutation(
  options?: MutationHookOptions<
    RiskDeclinePolicyChallengeMutationData,
    RiskDeclinePolicyChallengeMutationError,
    RiskDeclinePolicyChallengeMutationVariables
  >,
): UseMutationResult<
  RiskDeclinePolicyChallengeMutationData,
  RiskDeclinePolicyChallengeMutationError,
  RiskDeclinePolicyChallengeMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRiskDeclinePolicyChallengeMutation(client, options),
    ...options,
  });
}

export function mutationKeyRiskDeclinePolicyChallenge(): MutationKey {
  return ["@gram/client", "policyChallenges", "decline"];
}

export function buildRiskDeclinePolicyChallengeMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RiskDeclinePolicyChallengeMutationVariables,
  ) => Promise<RiskDeclinePolicyChallengeMutationData>;
} {
  return {
    mutationKey: mutationKeyRiskDeclinePolicyChallenge(),
    mutationFn: function riskDeclinePolicyChallengeMutationFn({
      request,
      security,
      options,
    }): Promise<RiskDeclinePolicyChallengeMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(riskPolicyChallengesDecline(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
